import * as React from 'react'
import { Button, Collapse } from 'react-bootstrap'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { BusinessForm } from 'api/common/constants'
import Footer from 'components/Footer'
import Header from 'components/Header'
import InputField from 'components/InputField'
import InputFieldPassword from 'components/InputFieldPassword'
import InputRadio2 from 'components/InputRadio2'
import { scrollToAnchor } from 'utilities/helper'
import { PreRegisterFormValues } from 'utilities/resources/formItemValues'
import { preRegisterFormItems } from 'utilities/resources/formItems'
import { getBusinessFormLabel } from 'utilities/resources/options'
import { PropertySetter } from 'utilities/typeUtility'
import {
  ErrorChecker,
  FormErrorMessage,
  FormItemType,
  birthdayValidator,
  checkInviteCode,
  emailValidator,
  isSelectedSummaryDocument,
  passwordValidator,
} from 'utilities/validators'

type StateValue = {
  formValues: PreRegisterFormValues
}

const SceneComponent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { type, hash } = useParams()
  const stateValue = React.useMemo(() => (location.state ?? {}) as StateValue, [location])
  const { formValues: prevFormValues } = stateValue

  const [formValues, setFormValues] = React.useState<PreRegisterFormValues>({
    email: prevFormValues?.email ?? '',
    businessForm: prevFormValues?.businessForm ?? `${BusinessForm.Individual}`,
    companyName: prevFormValues?.companyName ?? '',
    lastName: prevFormValues?.lastName ?? '',
    firstName: prevFormValues?.firstName ?? '',
    birthday: prevFormValues?.birthday ?? '',
    password: prevFormValues?.password ?? '',
    passwordConfirm: prevFormValues?.passwordConfirm ?? '',
    summaryDocumentKind: prevFormValues?.summaryDocumentKind ?? 0,
    summaryDocumentNumbers: prevFormValues?.summaryDocumentNumbers ?? [],
  })
  const setter = new PropertySetter(setFormValues)

  // エラー制御
  const [requireEntered, setRequireEntered] = React.useState(false)
  const [committed, setCommitted] = React.useState(false)
  const [errorValues, setErrorValues] = React.useState<FormErrorMessage<PreRegisterFormValues>>()
  const errorChecker = React.useMemo(() => new ErrorChecker<PreRegisterFormValues>(preRegisterFormItems), [])

  /**
   * 「登録内容を確認する」ボタン
   */
  const handleRegisterButton = () => {
    errorChecker.init()
    errorChecker.check(emailValidator(formValues?.email), 'email')
    errorChecker.check(birthdayValidator(formValues?.birthday), 'birthday')
    errorChecker.check(passwordValidator(formValues?.password), 'password')
    errorChecker.check(passwordValidator(formValues?.passwordConfirm, formValues?.password), 'passwordConfirm')
    setErrorValues(errorChecker.getErrorMessageData())
    if (errorChecker.isValid()) {
      setCommitted(true)
      const nextUrl = `/signUpPreRegisterConnectorConfirm/${type}/${hash}`
      navigate(nextUrl, { state: { formValues } })
    } else {
      scrollToAnchor(errorChecker.getFirstInvalidItemId())
    }
  }

  React.useEffect(() => {
    if (!checkInviteCode(type, hash) || !isSelectedSummaryDocument(prevFormValues)) {
      navigate('/')
    } else {
      const targetTypes: FormItemType[] = ['must']
      if (formValues.businessForm === `${BusinessForm.Company}`) {
        targetTypes.push('company')
      }
      setRequireEntered(errorChecker.isAllEntered(formValues, targetTypes))
    }
  }, [navigate, hash, type, errorChecker, formValues, prevFormValues])
  return (
    <>
      <Header title="コネクター仮登録" />
      <main>
        <section className="mx-4 my-5">
          <form>
            <InputField
              name={preRegisterFormItems.email.name}
              value={formValues.email}
              onChange={(e) => setter.set('email', e)}
              labelTop={preRegisterFormItems.email.label}
              type="email"
              placeholder="Emailアドレスを入れてください"
              errors={[errorValues?.email ?? '']}
              bold
            />
            <div className="mt-3">
              <div className="note">{preRegisterFormItems.businessForm.label}を法人または個人から選択ください。</div>
              <InputRadio2
                name={preRegisterFormItems.businessForm.name}
                label1={getBusinessFormLabel(BusinessForm.Individual)}
                label2={getBusinessFormLabel(BusinessForm.Company)}
                value1={`${BusinessForm.Individual}`}
                value2={`${BusinessForm.Company}`}
                checked={formValues.businessForm}
                onChange={(e) => setter.set('businessForm', e)}
                ariaControls="company-collapse"
              />
            </div>
            <Collapse in={formValues.businessForm === `${BusinessForm.Company}`}>
              <div id="company-collapse">
                <div className="mt-3">
                  <InputField
                    name={preRegisterFormItems.companyName.name}
                    value={formValues.companyName}
                    labelTop={preRegisterFormItems.companyName.label}
                    type="text"
                    maxLength={255}
                    placeholder="入力してください"
                    bold
                    onChange={(e) => setter.set('companyName', e)}
                    errors={[errorValues?.companyName ?? '']}
                  />
                </div>
              </div>
            </Collapse>
            <div className="mt-3 d-flex gap-3">
              <InputField
                name={preRegisterFormItems.lastName.name}
                value={formValues.lastName}
                labelTop={preRegisterFormItems.lastName.label}
                type="text"
                maxLength={20}
                placeholder="田中"
                bold
                onChange={(e) => setter.set('lastName', e)}
                errors={[errorValues?.lastName ?? '']}
              />
              <InputField
                name={preRegisterFormItems.firstName.name}
                value={formValues.firstName}
                labelTop={preRegisterFormItems.firstName.label}
                type="text"
                maxLength={20}
                placeholder="太郎"
                bold
                onChange={(e) => setter.set('firstName', e)}
                errors={[errorValues?.firstName ?? '']}
              />
            </div>
            <InputField
              name={preRegisterFormItems.birthday.name}
              value={formValues.birthday}
              onChange={(e) => setter.set('birthday', e)}
              className="mt-3"
              labelTop={preRegisterFormItems.birthday.label}
              type="number"
              placeholder="19900625"
              notes={['※半角数字で入力してください']}
              errors={[errorValues?.birthday ?? '']}
              bold
            />
            <InputFieldPassword
              name={preRegisterFormItems.password.name}
              value={formValues.password}
              onChange={(e) => setter.set('password', e)}
              className="mt-3"
              label={preRegisterFormItems.password.label}
              type="password"
              placeholder="password"
              notes={['※半角英数字を含む文字で入力してください', '※大文字と小文字を含む文字で入力してください', '※8文字以上で入力してください']}
              errors={[errorValues?.password ?? '']}
              bold
            />
            <InputFieldPassword
              name={preRegisterFormItems.passwordConfirm.name}
              value={formValues.passwordConfirm}
              onChange={(e) => setter.set('passwordConfirm', e)}
              className="mt-3"
              label={preRegisterFormItems.passwordConfirm.label}
              type="password"
              placeholder="password"
              errors={[errorValues?.passwordConfirm ?? '']}
              bold
            />
            <div className="mt-5">
              <Button variant="primary" className="w-100 py-4" onClick={handleRegisterButton} disabled={!(requireEntered && !committed)}>
                登録内容を確認する
              </Button>
            </div>
          </form>
        </section>
      </main>
      <Footer className="mt-5" />
    </>
  )
}

export default SceneComponent
